/**
 * Light color presets. Hex values are the LED's emitted color as a camera sees it at
 * night (whites are blackbody approximations), not UI swatch colors.
 */
export type ColorGroup = 'white' | 'color' | 'theme';

export interface ColorPreset {
  id: string;
  name: string;
  group: ColorGroup;
  /** Cycled node-by-node (or pair-by-pair). One entry = solid color. */
  colors: string[];
}

export const CUSTOM_COLOR_ID = 'custom';

const C = {
  red: '#FF2414',
  orange: '#FF6A0D',
  amber: '#FFA200',
  yellow: '#FFD21F',
  green: '#1EFF5A',
  teal: '#00F5C4',
  ice: '#7CD8FF',
  blue: '#2A5BFF',
  purple: '#8C3BFF',
  magenta: '#FF2BD6',
  pink: '#FF6FAE',
  lavender: '#B98CFF',
  mint: '#7DFFC0',
  white: '#FFE4CE', // 5000K
} as const;

export const COLOR_PRESETS: ColorPreset[] = [
  { id: 'warm-2700', name: 'Warm White', group: 'white', colors: [kelvinToHex(2700)] },
  { id: 'soft-3000', name: 'Soft White', group: 'white', colors: [kelvinToHex(3000)] },
  { id: 'neutral-4000', name: 'Neutral', group: 'white', colors: [kelvinToHex(4000)] },
  { id: 'cool-5000', name: 'Cool White', group: 'white', colors: [kelvinToHex(5000)] },
  { id: 'daylight-6500', name: 'Daylight', group: 'white', colors: [kelvinToHex(6500)] },

  { id: 'red', name: 'Red', group: 'color', colors: [C.red] },
  { id: 'orange', name: 'Orange', group: 'color', colors: [C.orange] },
  { id: 'amber', name: 'Gold', group: 'color', colors: [C.amber] },
  { id: 'yellow', name: 'Yellow', group: 'color', colors: [C.yellow] },
  { id: 'green', name: 'Green', group: 'color', colors: [C.green] },
  { id: 'teal', name: 'Teal', group: 'color', colors: [C.teal] },
  { id: 'ice', name: 'Ice Blue', group: 'color', colors: [C.ice] },
  { id: 'blue', name: 'Blue', group: 'color', colors: [C.blue] },
  { id: 'purple', name: 'Purple', group: 'color', colors: [C.purple] },
  { id: 'magenta', name: 'Magenta', group: 'color', colors: [C.magenta] },
  { id: 'pink', name: 'Pink', group: 'color', colors: [C.pink] },

  { id: 'christmas', name: 'Christmas', group: 'theme', colors: [C.red, C.green] },
  { id: 'candy-cane', name: 'Candy Cane', group: 'theme', colors: [C.red, C.white] },
  { id: 'winter', name: 'Winter', group: 'theme', colors: [C.ice, C.white] },
  { id: 'halloween', name: 'Halloween', group: 'theme', colors: [C.orange, C.purple] },
  { id: 'canada-day', name: 'Canada Day', group: 'theme', colors: [C.red, C.white] },
  { id: 'july-4', name: 'July 4th', group: 'theme', colors: [C.red, C.white, C.blue] },
  { id: 'valentines', name: "Valentine's", group: 'theme', colors: [C.red, C.pink, C.white] },
  { id: 'st-patricks', name: "St. Patrick's", group: 'theme', colors: [C.green, C.white, C.amber] },
  { id: 'easter', name: 'Easter', group: 'theme', colors: [C.pink, C.lavender, C.mint, C.ice] },
  { id: 'fall', name: 'Fall', group: 'theme', colors: [C.orange, C.amber, C.red] },
  { id: 'hanukkah', name: 'Hanukkah', group: 'theme', colors: [C.blue, C.white] },
  { id: 'rainbow', name: 'Rainbow', group: 'theme', colors: [C.red, C.orange, C.yellow, C.green, C.blue, C.purple] },
];

export const DEFAULT_COLOR_ID = 'warm-2700';

export function findPreset(id: string, customHex: string): ColorPreset {
  if (id === CUSTOM_COLOR_ID) {
    return { id, name: 'Custom', group: 'color', colors: [customHex] };
  }
  return COLOR_PRESETS.find((p) => p.id === id) ?? COLOR_PRESETS[0];
}

/** Tanner Helland's blackbody approximation, good enough for 1000K–12000K. */
export function kelvinToHex(kelvin: number): string {
  const t = kelvin / 100;
  const r = t <= 66 ? 255 : 329.698727446 * Math.pow(t - 60, -0.1332047592);
  const g =
    t <= 66
      ? 99.4708025861 * Math.log(t) - 161.1195681661
      : 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  return rgbToHex([r, g, b]);
}

export function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const v = parseInt(h.slice(0, 6), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

export function rgbToHex([r, g, b]: [number, number, number]): string {
  const c = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

/** Float RGBA (0..1) for Skia.Color / gradients. */
export function rgba(hex: string, alpha = 1): Float32Array {
  const [r, g, b] = hexToRgb(hex);
  return Float32Array.of(r / 255, g / 255, b / 255, Math.max(0, Math.min(1, alpha)));
}

/** Linear-light RGB (0..1) of an sRGB hex color, for physically based light math. */
export function linearColor(hex: string): [number, number, number] {
  const toLin = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = hexToRgb(hex);
  return [toLin(r), toLin(g), toLin(b)];
}

export function mixHex(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex([0, 1, 2].map((i) => ca[i] + (cb[i] - ca[i]) * t) as [number, number, number]);
}

export function hsvToHex(h: number, s: number, v: number): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };
  return rgbToHex([f(5) * 255, f(3) * 255, f(1) * 255]);
}

export function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const [r, g, b] = hexToRgb(hex).map((n) => n / 255);
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  let h = 0;
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

/** A human description of a color for AI prompts, e.g. "warm white (2700K)". */
export function describePreset(preset: ColorPreset): string {
  switch (preset.id) {
    case 'warm-2700':
      return 'warm white (2700K, cozy golden-white like incandescent)';
    case 'soft-3000':
      return 'soft white (3000K)';
    case 'neutral-4000':
      return 'neutral white (4000K)';
    case 'cool-5000':
      return 'cool white (5000K)';
    case 'daylight-6500':
      return 'crisp daylight white (6500K, slightly bluish)';
  }
  if (preset.colors.length === 1) {
    return `${preset.name.toLowerCase()} (${preset.colors[0]})`;
  }
  const names = preset.colors.map(colorName);
  return `alternating ${names.join(', ')} (${preset.name} theme), repeating in that order`;
}

const NAMED: Record<string, string> = Object.fromEntries(
  Object.entries(C).map(([name, hex]) => [hex, name === 'ice' ? 'ice blue' : name]),
);

function colorName(hex: string): string {
  return NAMED[hex.toUpperCase()] ?? hex;
}
